import type { RoomView } from "../../shared/game";
import { PH_COUNTDOWN_MS, PH_ZERO_GRACE_MS, phLaunchCountdown, PH_LAUNCH_COUNTDOWN_MS, PH_FIRST_LAUNCH_COUNTDOWN_MS } from "../../shared/planetHoppers";
import { SITE_TUNING } from "./siteTuning";

export const PH_MUSIC = [
    { file: "space-1.mp3", start: 9 }, { file: "space-2.mp3", start: 0 },
    { file: "space-3.mp3", start: 0 }, { file: "space-4.mp3", start: 30 },
    { file: "space-5.mp3", start: 125 },
];
const BASE = "/audio/planet-hoppers/";
const tuning = SITE_TUNING.planetHoppersAudio;
export function nextPHTrack(history: number[], random = Math.random): number {
    const eligible = PH_MUSIC.map((_, i) => i).filter(i => !history.slice(-2).includes(i));
    return eligible[Math.min(eligible.length - 1, Math.floor(random() * eligible.length))];
}
type Deck = { audio: HTMLAudioElement; index: number; started: boolean; from: number };
/** The host owns the room speakers. Render updates never restart a song or cue. */
export class PlanetHoppersAudio {
    private history: number[] = [];
    private positions = new Map<number, number>();
    private current?: Deck;
    private incoming?: Deck;
    private transition?: HTMLAudioElement;
    private cue?: HTMLAudioElement;
    private cueKey = "";
    private answerRound = "";
    private stopAt: number | null = null;
    private round = "";
    private room: RoomView | null = null;
    private offset = 0;
    private dead = false;
    private pending = new WeakSet<HTMLAudioElement>();
    private blocked = new WeakSet<HTMLAudioElement>();
    private context?: AudioContext;
    private cueSource?: MediaElementAudioSourceNode;
    private cueGain?: GainNode;
    constructor() {
        document.addEventListener("pointerdown", this.unlock);
        document.addEventListener("keydown", this.unlock);
    }
    private unlock = () => {
        this.blocked = new WeakSet();
        if (this.context?.state === "suspended") void this.context.resume();
        if (this.room) this.update(this.room, Date.now() + this.offset);
    };
    private play(audio: HTMLAudioElement) {
        if (!audio.paused || audio.ended || this.pending.has(audio) || this.blocked.has(audio)) return;
        this.pending.add(audio);
        void audio.play().catch(error => {
            if (error?.name === "NotAllowedError") this.blocked.add(audio);
            else if (error?.name !== "AbortError") {
                this.blocked.add(audio);
                console.warn(`[planet-hoppers:audio] playback_failed file=${audio.src.split("/").pop()} error=${error?.name}`);
            }
        }).finally(() => this.pending.delete(audio));
    }
    private deck(): Deck {
        const index = nextPHTrack(this.history);
        const audio = new Audio(BASE + PH_MUSIC[index].file);
        audio.preload = "auto"; audio.volume = 0;
        const deck: Deck = { audio, index, started: false, from: this.positions.get(index) ?? PH_MUSIC[index].start };
        audio.addEventListener("loadedmetadata", () => {
            if (deck.from >= audio.duration - tuning.crossfadeSeconds) deck.from = PH_MUSIC[index].start;
            audio.currentTime = deck.from;
        }, { once: true });
        audio.load();
        return deck;
    }
    private releaseDeck(deck?: Deck) {
        if (!deck) return;
        if (deck.started) this.positions.set(deck.index, deck.audio.ended || deck.audio.currentTime >= deck.audio.duration - tuning.crossfadeSeconds ? PH_MUSIC[deck.index].start : deck.audio.currentTime);
        this.release(deck.audio);
    }
    private start(deck: Deck) {
        this.play(deck.audio);
        if (!deck.started && !deck.audio.paused) {
            deck.started = true;
            this.history = [...this.history.slice(-1), deck.index];
        }
    }
    private release(audio?: HTMLAudioElement) {
        if (!audio) return;
        audio.pause(); audio.removeAttribute("src"); audio.load();
    }
    private releaseCue() {
        this.release(this.cue); this.cue = undefined;
        this.cueSource?.disconnect(); this.cueGain?.disconnect();
        this.cueSource = undefined; this.cueGain = undefined;
    }
    private boostReveal(audio: HTMLAudioElement) {
        // HTML media volume caps at 1. A gain node applies the reveal level above the media volume ceiling.
        this.context ??= new AudioContext();
        this.cueSource = this.context.createMediaElementSource(audio);
        this.cueGain = this.context.createGain();
        this.cueGain.gain.value = tuning.revealGain;
        this.cueSource.connect(this.cueGain); this.cueGain.connect(this.context.destination);
        if (this.context.state === "suspended") void this.context.resume();
    }
    prepareLaunch(): Promise<void> {
        const audio = this.transition;
        if (!audio || audio.readyState >= 3 || audio.error) return Promise.resolve();
        // Never hold the classroom indefinitely if a media request fails or stalls.
        return new Promise(resolve => {
            const finish = () => {
                clearTimeout(timeout);
                audio.removeEventListener("canplay", finish);
                audio.removeEventListener("error", finish);
                resolve();
            };
            const timeout = setTimeout(finish, tuning.transitionPreloadTimeoutMs);
            audio.addEventListener("canplay", finish, {once:true});
            audio.addEventListener("error", finish, {once:true});
        });
    }
    update(room: RoomView, now: number) {
        if (this.dead) return;
        this.room = room; this.offset = now - Date.now();
        const ph = room.planetHoppers;
        if (!room.viewer.isHost || !ph) { this.stop(); return; }
        const launch = ph.stage === "question" && phLaunchCountdown(ph.openedAt, now, ph.index === 0) > 0;
        const launchEnd = ph.openedAt + (ph.index === 0 ? PH_FIRST_LAUNCH_COUNTDOWN_MS : PH_LAUNCH_COUNTDOWN_MS);
        if (this.round !== ph.roundId) {
            this.round = ph.roundId; this.stopAt = null;
            this.releaseDeck(this.current); this.releaseDeck(this.incoming);
            this.current = this.incoming = undefined;
            this.release(this.transition); this.transition = undefined;
            if (ph.stage === "question") this.current = this.deck();
            if (launch || ph.awaitingHost) {
                this.transition = new Audio(BASE + "transition.mp3");
                this.transition.preload = "auto";
                this.transition.playbackRate = tuning.transitionRate;
                this.transition.load();
            }
        }
        // Prepare the soundtrack behind the black readiness cover, then launch together.
        if (ph.awaitingHost) { this.releaseCue(); this.cueKey = ""; return; }
        // Transition has its own channel: its tail cannot block a countdown or answer cue.
        if (this.transition) {
            if (ph.stage !== "question" || this.transition.ended || now >= launchEnd + tuning.transitionTailMs) {
                this.release(this.transition); this.transition = undefined;
            } else {
                this.transition.volume = launch ? 1 : Math.max(0, 1 - (now - launchEnd) / tuning.transitionTailMs);
                this.play(this.transition);
            }
        }
        const final = ph.stage === "question" && !launch && ph.finalCountdown && ph.endsAt !== null && ph.endsAt - now <= PH_COUNTDOWN_MS + PH_ZERO_GRACE_MS && now < ph.endsAt;
        const key = final ? `${ph.roundId}:final` : ph.stage === "reveal" ? `${ph.roundId}:answer` : "";
        if (key !== this.cueKey) {
            this.releaseCue();
            if (key && !(ph.stage === "reveal" && this.answerRound === ph.roundId)) {
                this.cue = new Audio(BASE + (final ? "time-running-out.mp3" : "answer-confirmed.mp3"));
                this.cue.volume = 1;
                this.cue.loop = !!final;
                if (!final) { this.cue.playbackRate = tuning.revealRate; this.answerRound = ph.roundId; this.boostReveal(this.cue); }
            }
            this.cueKey = key;
        }
        if (this.cue) this.play(this.cue);
        if (this.stopAt === null && ph.stage !== "question") this.stopAt = now;
        if (this.stopAt !== null) {
            const gain = Math.max(0, 1 - (now - this.stopAt) / tuning.stopFadeMs);
            for (const deck of [this.current, this.incoming]) {
                if (!deck) continue;
                deck.audio.volume = Math.min(deck.audio.volume, tuning.musicVolume * gain);
                if (gain === 0) deck.audio.pause();
            }
            return;
        }
        // Preload during launch, but start music only when the question is visible.
        if (launch) return;
        this.current ??= this.deck();
        const current = this.current.audio;
        this.start(this.current);
        // The transition's fading tail may overlap the newly visible question.
        if (!this.incoming && this.current.started && current.readyState >= 1) this.incoming = this.deck();
        const next = this.incoming?.audio;
        if (next && current.duration - current.currentTime <= tuning.crossfadeSeconds && next.readyState >= 3) {
            this.start(this.incoming!);
            const elapsed = Math.max(0, next.currentTime - this.incoming!.from);
            const mix = Math.min(1, elapsed / tuning.crossfadeSeconds);
            current.volume = tuning.musicVolume * (1 - mix); next.volume = tuning.musicVolume * mix;
            if (mix >= 1 || current.ended) {
                this.releaseDeck(this.current); this.current = this.incoming; this.incoming = undefined;
                this.current!.audio.volume = tuning.musicVolume;
            }
        } else current.volume = tuning.musicVolume;
    }
    private stop() {
        this.release(this.current?.audio); this.release(this.incoming?.audio); this.release(this.transition); this.releaseCue();
        this.current = this.incoming = undefined; this.transition = undefined; this.cueKey = ""; this.round = "";
    }
    destroy() {
        this.dead = true; this.stop();
        if (this.context) void this.context.close();
        document.removeEventListener("pointerdown", this.unlock);
        document.removeEventListener("keydown", this.unlock);
    }
}


